import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StoreSettings } from '../../../Types/StoreTypes';
import { DLPState } from '../../../Types/Enums';

const initialState: StoreSettings = {
    systemStatus: 'running',
    dataProtectionText: '',
    demo: false,
    betaActive: false,
    icapActive: false,
    features: {
        documentUpload: false,
    },
    sensitivityLabels: [],
    localGroups: true,
    bugreporting: false,
    registrationEnabled: false,
    version: '0.0.0',
    apiBaseUrl: '',
    websocketUrl: '',
    oidcConfiurationUrl: '',
    oidcAuthority: '',
    oidcClientId: '',
    oidcUserIdentifier: 'upn',
    oidcLogoutUrl: '',
    changelogs: [],
    changelogHash: '',
    maxCosts: 0.0,
    licences: 0,
    apiKeyLicences: 0,
    userStatistic: true,
    prePaidNotifications: false,
    proxyUrlHttp: '',
    proxyUrlHttps: '',
    proxyExceptions: '',
    proxyCertificatePem: '',
    icapAddress: '',
    summaryModel: '',
    defaultModel: '',
    embeddingModel: '',
    embeddingModelDlp: DLPState.dlp_on,
    dlpRegexes: [],
    dlpEntities: ['CREDIT_CARD', 'IBAN_CODE', 'CRYPTO', 'PHONE_NUMBER', 'EMAIL_ADDRESS', 'PERSON', 'IP_ADDRESS'],
    activeDlpProvider: 'presidio',
    availableDlpProviders: {},
    availableDlpEntities: {},
    allowList: [],
    publicVersion: '0.0.0',
    supportMailAddress: '',
    prePaidActive: false,
    selfUpdateActive: false,
    supportLoginEnabled: false,
    helpLinkVisible: true,
    licenseKey: '',
    accountId: '',
    releaseChannel: 'stable',
    instanceSystemPrompt: '',
    bestPracticeSystemPrompt: '',
    themeIdentifier: '',
    brandingTitle: null,
    loggerSettings: {},
    chatDuration: 'infinite',
    deleteUserAfter: 0,
    openaiDocumentProcessingEnabled: true,
    reloadSettings: false,
    debugMode: false,
    ttsModel: '',
    transcribeModel: '',
    allowedProtocols: ['http', 'https', 'mailto', 'tel', 'sandbox', 'data'],
    additionalAllowedProtocols: [],
    knowledgebaseSearchMode: 'hybrid',
    hybridSearchVectorWeight: 0.5,
    hybridSearchTextWeight: 0.5,
    chunkSize: 2000,
    chunkOverlap: 500,
    dsgvoVersionTimestamp: 0,
    userSyncPullInterval: null,
    scimDirection: null,
    favicon: null,
    theme: {
        lightThemeImage: null,
        darkThemeImage: null,
    },
};

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        setSettings: (state, action: PayloadAction<Partial<StoreSettings>>) => {
            return { ...state, ...action.payload };
        },
        setDemo: (state, action: PayloadAction<StoreSettings['demo']>) => {
            state.demo = action.payload;
        },
        setLocalGroups: (state, action: PayloadAction<StoreSettings['localGroups']>) => {
            state.localGroups = action.payload;
        },
        setApiBaseUrl: (state, action: PayloadAction<StoreSettings['apiBaseUrl']>) => {
            state.apiBaseUrl = action.payload;
        },
        setWebsocketUrl: (state, action: PayloadAction<StoreSettings['websocketUrl']>) => {
            state.websocketUrl = action.payload;
        },
        setOidcConfigurationUrl: (state, action: PayloadAction<StoreSettings['oidcConfiurationUrl']>) => {
            state.oidcConfiurationUrl = action.payload;
        },
        setOidcLogoutUrl: (state, action: PayloadAction<StoreSettings['oidcLogoutUrl']>) => {
            state.oidcLogoutUrl = action.payload;
        },
        setOidcAuthority: (state, action: PayloadAction<StoreSettings['oidcAuthority']>) => {
            state.oidcAuthority = action.payload;
        },
        setOidcClientId: (state, action: PayloadAction<StoreSettings['oidcClientId']>) => {
            state.oidcClientId = action.payload;
        },
        setOidcUserIdentifier: (state, action: PayloadAction<StoreSettings['oidcUserIdentifier']>) => {
            state.oidcUserIdentifier = action.payload;
        },
        setBugReporting: (state, action: PayloadAction<StoreSettings['bugreporting']>) => {
            state.bugreporting = action.payload;
        },
        setRegistrationEnabled: (state, action: PayloadAction<StoreSettings['registrationEnabled']>) => {
            state.registrationEnabled = action.payload;
        },
        setVersion: (state, action: PayloadAction<StoreSettings['version']>) => {
            state.version = action.payload;
        },
        setDataProtectionText: (state, action: PayloadAction<StoreSettings['dataProtectionText']>) => {
            state.dataProtectionText = action.payload;
        },
        setFeatureDocumentUpload: (state, action: PayloadAction<StoreSettings['features']['documentUpload']>) => {
            state.features.documentUpload = action.payload;
        },
        setChangelogs: (state, action: PayloadAction<StoreSettings['changelogs']>) => {
            state.changelogs = action.payload;
        },
        setChangelogHash: (state, action: PayloadAction<StoreSettings['changelogHash']>) => {
            state.changelogHash = action.payload;
        },
        setBetaActive: (state, action: PayloadAction<StoreSettings['betaActive']>) => {
            state.betaActive = action.payload;
        },
        setSensitivityLabels: (state, action: PayloadAction<StoreSettings['sensitivityLabels']>) => {
            state.sensitivityLabels = action.payload;
        },
        setSystemStatus: (state, action: PayloadAction<StoreSettings['systemStatus']>) => {
            if (state.systemStatus === 'updating' && action.payload === 'running') {
                window.location.reload();
            }
            state.systemStatus = action.payload;
        },
        setReleaseChannel: (state, action: PayloadAction<StoreSettings['releaseChannel']>) => {
            state.releaseChannel = action.payload;
        },
        setReloadSettings: (state, action: PayloadAction<StoreSettings['reloadSettings']>) => {
            state.reloadSettings = action.payload;
        },
        toggleDebugMode: (state) => {
            state.debugMode = !state.debugMode;
        },
    },
});

export const {
    setSettings,
    setDemo,
    setLocalGroups,
    setApiBaseUrl,
    setWebsocketUrl,
    setOidcConfigurationUrl,
    setOidcLogoutUrl,
    setOidcAuthority,
    setOidcClientId,
    setOidcUserIdentifier,
    setBugReporting,
    setRegistrationEnabled,
    setVersion,
    setDataProtectionText,
    setFeatureDocumentUpload,
    setChangelogs,
    setChangelogHash,
    setBetaActive,
    setSensitivityLabels,
    setSystemStatus,
    setReleaseChannel,
    setReloadSettings,
    toggleDebugMode,
} = settingsSlice.actions;

export default settingsSlice.reducer;
